import { useEffect, useState } from 'react';
import { Reply } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import type { Message } from '@/types/message-types';
import { useTenantSelection } from '@/contexts/TenantSelectionContext';
import { useSignedMediaUrl } from '@/hooks/useSignedMediaUrl';

interface QuotedMessageProps {
  repliedToWhatsappId: string;
  conversationId: string;
  tenantId?: string;
  onScrollToMessage?: (whatsappMessageId: string) => void;
}

export function QuotedMessage({ repliedToWhatsappId, conversationId, tenantId, onScrollToMessage }: QuotedMessageProps) {
  const [quotedMsg, setQuotedMsg] = useState<Message | null>(null);
  const [loading, setLoading] = useState(true);
  const { selectedTenant } = useTenantSelection();
  // Hook must be called unconditionally (React rules) — null input returns null
  const { signedUrl: signedMediaUrl } = useSignedMediaUrl(quotedMsg?.media_url ?? null);

  useEffect(() => {
    const fetchQuotedMessage = async () => {
      if (!tenantId || !repliedToWhatsappId) {
        setLoading(false);
        return;
      }

      try {
        // Get tenant schema from context (not constructed from UUID)
        const tenantSchema = selectedTenant?.schema_name;
        if (!tenantSchema) {
          setLoading(false);
          return;
        }

        // Phase 1 typed RPC: tenant-isolated, no raw SQL.
        const { data, error } = await supabase.rpc('get_quoted_message', {
          p_tenant_schema: tenantSchema,
          p_whatsapp_message_id: repliedToWhatsappId,
        });

        if (error) {
          console.error('Error fetching quoted message:', error);
          setLoading(false);
          return;
        }

        // Returns the row JSONB or NULL.
        if (data) {
          setQuotedMsg(data as Message);
        }
      } catch (error) {
        console.error('Error in fetchQuotedMessage:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchQuotedMessage();
  }, [repliedToWhatsappId, conversationId, tenantId]);

  // Don't show loading state - just wait silently
  if (loading) {
    return null;
  }

  // Message not found (may have been deleted) - hide gracefully
  if (!quotedMsg) {
    return null;
  }

  const truncateContent = (content: string) => {
    if (content.length > 50) return content.substring(0, 50) + '...';
    return content;
  };

  const handleClick = () => {
    // V1300 Phase 2: Scroll to original message when quoted message is clicked
    console.log('🔍 QuotedMessage clicked:', {
      hasCallback: !!onScrollToMessage,
      whatsappMessageId: quotedMsg?.external_id
    });
    if (onScrollToMessage && quotedMsg.external_id) {
      onScrollToMessage(quotedMsg.external_id);
    }
  };

  return (
    <div
      onClick={handleClick}
      className="mb-2 p-2 bg-muted/50 dark:bg-muted/30 rounded-md border-l-4 border-primary/30 cursor-pointer hover:bg-muted/70 dark:hover:bg-muted/40 transition-colors"
    >
      <div className="flex items-center gap-1 mb-0.5">
        <Reply className="h-3 w-3 text-muted-foreground" />
      </div>

      {/* Content Preview - V1300 Phase 2: Show rich previews for efficiency */}
      <div className="text-xs text-muted-foreground">
        {/* Image: Show thumbnail (signed URL) */}
        {quotedMsg.message_type === 'image' && signedMediaUrl && (
          <img
            src={signedMediaUrl}
            alt="Quoted"
            className="w-20 h-20 object-cover rounded"
          />
        )}

        {/* Video: Show thumbnail with play icon (signed URL) */}
        {quotedMsg.message_type === 'video' && signedMediaUrl && (
          <div className="relative w-20 h-20">
            <video
              src={signedMediaUrl}
              className="w-full h-full object-cover rounded"
            />
            <div className="absolute inset-0 flex items-center justify-center bg-black/30 rounded">
              <span className="text-white text-2xl">▶</span>
            </div>
          </div>
        )}

        {/* Audio: Show audio icon + duration if available */}
        {quotedMsg.message_type === 'audio' && (
          <div className="flex items-center gap-2">
            <span className="text-base">🎤</span>
            <span>Audio</span>
          </div>
        )}

        {/* Document: Show filename */}
        {quotedMsg.message_type === 'document' && (
          <div className="flex items-center gap-2">
            <span className="text-base">📄</span>
            <span className="truncate max-w-[150px]">
              {quotedMsg.metadata?.original_filename || 'Document'}
            </span>
          </div>
        )}

        {/* Text: Show first 50 chars */}
        {quotedMsg.message_type === 'text' && (
          <span className="truncate block">{truncateContent(quotedMsg.content)}</span>
        )}

        {/* Other types: Show type label */}
        {!['image', 'video', 'audio', 'document', 'text'].includes(quotedMsg.message_type) && (
          <span>{quotedMsg.message_type}</span>
        )}
      </div>
    </div>
  );
}
